/** @jsxImportSource @emotion/react */
import {
  createStyles,
  Icon,
  Input,
  Typography,
  useTheme,
  Modal,
  ListItem,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { useMemo, useReducer, useState } from 'react';
import { useAsyncFn } from 'react-use';
import { Header } from '../../../../components';
import { useSession } from '../../../../context/Session';
import { Role, isNewRole } from '../../../../types';
import { PermissionList } from '../../../components/PermissionList';
import { saveGroup, createGroup, deleteGroup } from '../../api';

interface Props {
  role: Role | 'new';
  permissions: string[];
  cancel: () => void;
}

interface UpdateRoleAction {
  action: string;
  payload: string | string[];
}

export const RoleDetails = ({ role: propRole, permissions, cancel }: Props) => {
  const session = useSession();
  const styles = useStyles();
  const { palette, spacing } = useTheme();
  const canEditRoles =
    session.authenticated && session.permissions.includes('edit_groups');

  const defaultRole: Role = useMemo(
    () =>
      propRole === 'new'
        ? {
            name: 'new',
            permissions: [],
          }
        : propRole,
    [propRole]
  );

  const [role, updateRole] = useReducer(
    (currentRole: Role, { action, payload }: UpdateRoleAction) => {
      return {
        ...currentRole,
        [action]: payload,
      } as Role;
    },
    defaultRole
  );

  const [{ loading: saveLoading }, save] = useAsyncFn(
    () => saveGroup(role),
    [role]
  );

  const [{ loading: deleteLoading }, remove] = useAsyncFn(
    () => deleteGroup(role.name).then(() => cancel()),
    [role]
  );

  const [{ loading: createLoading }, create] = useAsyncFn(async () => {
    if (!isNewRole(defaultRole)) {
      throw Error('Creating existing Role');
    }
    await createGroup(role);
    cancel();
  }, [role]);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  return (
    <div>
      <Header>
        <div css={styles.headerContent}>
          <div css={styles.titleContainer}>
            <div css={styles.back} onClick={cancel}>
              <Typography variant="subtitle" color={palette.grey.x300}>
                User Roles
              </Typography>
            </div>
            <div css={styles.chevron}>
              <Icon name="chevron" size={spacing.xxs} />
            </div>
            <Typography variant="subtitle">
              {isNewRole(defaultRole) ? 'New role' : role.name}
            </Typography>
          </div>
          <UtilityButton text="Cancel" variant="secondary" onClick={cancel} />
          {isNewRole(defaultRole) && canEditRoles && (
            <UtilityButton
              text="Create role"
              variant="primary"
              loading={createLoading}
              disabled={!role.name}
              onClick={create}
            />
          )}
          {!isNewRole(defaultRole) && canEditRoles && (
            <UtilityButton
              text="Update role"
              variant="primary"
              loading={saveLoading}
              onClick={save}
            />
          )}
        </div>
      </Header>
      <div css={styles.wrappingContainer}>
        <div css={[styles.container, styles.inputWrapper]}>
          <Input
            label="User role name"
            value={role.name}
            onChange={(e) =>
              updateRole({ action: 'name', payload: e.target.value })
            }
            disabled={!isNewRole(defaultRole) || !canEditRoles}
          />
          {!isNewRole(defaultRole) && canEditRoles && (
            <div css={deleteLoading && styles.removeLoading}>
              <ListItem
                title="Remove role"
                icon="trash"
                onClick={() => {
                  setDeleteModalOpen(true);
                }}
                disabled={deleteLoading}
                action={deleteLoading && <Icon name="spinner" />}
              />
            </div>
          )}
        </div>
        <div css={styles.container}>
          <PermissionList
            permissions={permissions.map((p) => ({
              name: p,
              checked: role.permissions.includes(p),
            }))}
            selectedInstancePermissions={role.permissions}
            updatePermissions={
              canEditRoles
                ? (newPermissions) => {
                    updateRole({
                      action: 'permissions',
                      payload: newPermissions,
                    });
                  }
                : undefined
            }
          />
        </div>
      </div>
      {deleteModalOpen && (
        <Modal
          title="Are you sure?"
          subtitle="Removing a role can not be undone."
          secondaryButtonProps={{
            text: 'Cancel',
            onClick: () => setDeleteModalOpen(false),
          }}
          primaryButtonProps={{
            text: 'Confirm',
            onClick: () => remove(),
          }}
          closeModal={() => setDeleteModalOpen(false)}
        />
      )}
    </div>
  );
};

const useStyles = createStyles(({ spacing, palette }) => ({
  wrappingContainer: {
    width: '100%',
    display: 'flex',
    gap: spacing.xs,
  },
  container: {
    background: palette.white,
    borderRadius: spacing.xxxs,
    flex: 1,
  },
  inputWrapper: {
    marginBottom: 'auto',
    padding: spacing.xxs,
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: spacing.xxs,
    '& li': {
      borderRadius: spacing.xxxs,
      border: `1px solid ${palette.grey.x200}`,
      ':hover': {
        borderColor: palette.grey.x600,
      },
    },
  },
  headerContent: {
    display: 'grid',
    gap: spacing.xxs,
    gridTemplateColumns: '1fr auto auto',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  back: {
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
      textDecorationColor: palette.grey.x300,
    },
  },
  chevron: {
    transform: 'rotate(180deg)',
    padding: `0 ${spacing.xxs}`,
    svg: { fill: 'none', stroke: palette.grey.x300 },
  },
  removeLoading: { pointerEvents: 'none' },
}));
